import { paloSortDefault } from '@synergy/components-palo';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface DashboardSort {
    key: string;
    order: 'asc' | 'desc';
}

/**
 * The sorting of a cockpit tab.
 *
 * The cockpit asks one endpoint for all five document types with a single
 * sort, so the order a tab opens in is put into the request above the tabs
 * (see the outlet). What is read here is the arrow: the column the user chose,
 * or the one the list opens in order of (LST-S16, #2234).
 *
 * Only columns whose key both search engines can serve carry a `sortKey` — a
 * sort the engine does not know is dropped without an error, and the list then
 * shows an arrow over rows that never moved.
 */
export const useDashboardSort = (
    listKey: string
): {
    sort?: DashboardSort;
    onSortChange: (key: string, order: 'asc' | 'desc') => void;
} => {
    const [searchParams, setSearchParams] = useSearchParams();
    const key = searchParams.get('sort');

    const onSortChange = useCallback(
        (nextKey: string, nextOrder: 'asc' | 'desc') => {
            const next = new URLSearchParams(searchParams);

            next.set('sort', nextKey);
            next.set('order', nextOrder);
            next.set('page', '1');

            setSearchParams(next, { replace: true });
        },
        [searchParams, setSearchParams]
    );

    const fallback = paloSortDefault(listKey);

    return {
        sort: key
            ? { key, order: 'desc' === searchParams.get('order') ? 'desc' : 'asc' }
            : { key: fallback.key, order: fallback.order },
        onSortChange,
    };
};
